import { mergeUniqueGames } from '../../utils/liveCasinoCategories'
import type { PkCasinoCatalogConfig } from './playKaroCasinoCatalogTypes'

export type CatalogFetchMode = 'guest' | 'auth'

type CacheEntry = {
  games: Record<string, unknown>[]
  ts: number
}

const CACHE_TTL_MS = 5 * 60 * 1000
const FETCH_CONCURRENCY = 6
const TASK_TIMEOUT_MS = 15000

const memoryCache = new Map<string, CacheEntry>()

type CatalogTask =
  | { kind: 'search'; term: string }
  | { kind: 'provider'; provider: string; type: string }

export type CatalogLoadHandlers = {
  searchApi: (term: string) => Promise<Record<string, unknown>[]>
  listApi: (provider: string, type: string) => Promise<Record<string, unknown>[]>
}

type InflightEntry = {
  promise: Promise<Record<string, unknown>[]>
  games: Record<string, unknown>[]
  listeners: Set<(games: Record<string, unknown>[]) => void>
}

const inflightLoads = new Map<string, InflightEntry>()

function cacheKey(catalogId: string, mode: CatalogFetchMode): string {
  return `${catalogId}:${mode}`
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('catalog-task-timeout')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

export function getCachedCatalogGames(
  catalogId: string,
  mode: CatalogFetchMode,
): Record<string, unknown>[] | null {
  const hit = memoryCache.get(cacheKey(catalogId, mode))
  if (!hit) return null
  if (Date.now() - hit.ts > CACHE_TTL_MS) {
    memoryCache.delete(cacheKey(catalogId, mode))
    return null
  }
  return hit.games
}

export function setCachedCatalogGames(
  catalogId: string,
  mode: CatalogFetchMode,
  games: Record<string, unknown>[],
): void {
  memoryCache.set(cacheKey(catalogId, mode), { games, ts: Date.now() })
}

function notifyListeners(entry: InflightEntry): void {
  const snapshot = entry.games
  entry.listeners.forEach((listener) => listener(snapshot))
}

async function runTaskPool(
  tasks: CatalogTask[],
  limit: number,
  run: (task: CatalogTask) => Promise<Record<string, unknown>[]>,
  onResult: (games: Record<string, unknown>[]) => void,
): Promise<void> {
  if (!tasks.length) return
  let cursor = 0

  const worker = async () => {
    while (cursor < tasks.length) {
      const index = cursor
      cursor += 1
      const task = tasks[index]
      try {
        const games = await run(task)
        if (games.length) onResult(games)
      } catch {
        // ignore failed or timed-out provider/search fetches
      }
    }
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => worker())
  await Promise.all(workers)
}

function buildTasks(config: PkCasinoCatalogConfig): CatalogTask[] {
  const searchTasks: CatalogTask[] = config.searchTerms.map((term) => ({ kind: 'search', term }))
  const providerTasks: CatalogTask[] = config.listProviders.flatMap((provider) =>
    config.listTypes.map((type) => ({ kind: 'provider', provider, type })),
  )
  return [...searchTasks, ...providerTasks]
}

async function runCatalogLoad(
  config: PkCasinoCatalogConfig,
  mode: CatalogFetchMode,
  handlers: CatalogLoadHandlers,
  entry: InflightEntry,
): Promise<Record<string, unknown>[]> {
  const merged: Record<string, unknown>[] = []

  const appendBatch = (batch: Record<string, unknown>[]) => {
    if (!batch.length) return
    const next = mergeUniqueGames([merged, batch]).filter(config.filterGame)
    merged.length = 0
    merged.push(...next)
    entry.games = next
    notifyListeners(entry)
  }

  await runTaskPool(
    buildTasks(config),
    FETCH_CONCURRENCY,
    async (task) => {
      if (task.kind === 'search') {
        return withTimeout(handlers.searchApi(task.term), TASK_TIMEOUT_MS)
      }
      return withTimeout(handlers.listApi(task.provider, task.type), TASK_TIMEOUT_MS)
    },
    appendBatch,
  )

  entry.games = merged
  notifyListeners(entry)
  setCachedCatalogGames(config.catalogId, mode, merged)
  return merged
}

function startInflightLoad(
  config: PkCasinoCatalogConfig,
  mode: CatalogFetchMode,
  handlers: CatalogLoadHandlers,
): InflightEntry {
  const key = cacheKey(config.catalogId, mode)
  const entry: InflightEntry = {
    games: [],
    listeners: new Set(),
    promise: Promise.resolve([]),
  }

  entry.promise = runCatalogLoad(config, mode, handlers, entry).finally(() => {
    inflightLoads.delete(key)
  })

  inflightLoads.set(key, entry)
  return entry
}

export type CatalogSubscription = {
  promise: Promise<Record<string, unknown>[]>
  unsubscribe: () => void
}

/** Subscribe to a shared catalog load (reuses prefetch / in-flight requests). */
export function subscribeCatalogGamesLoad(
  config: PkCasinoCatalogConfig,
  mode: CatalogFetchMode,
  handlers: CatalogLoadHandlers,
  onProgress: (games: Record<string, unknown>[]) => void,
): CatalogSubscription {
  const cached = getCachedCatalogGames(config.catalogId, mode)
  if (cached?.length) {
    onProgress(cached)
    return {
      promise: Promise.resolve(cached),
      unsubscribe: () => undefined,
    }
  }

  const key = cacheKey(config.catalogId, mode)
  let entry = inflightLoads.get(key)
  if (!entry) {
    entry = startInflightLoad(config, mode, handlers)
  }

  entry.listeners.add(onProgress)
  if (entry.games.length) {
    onProgress(entry.games)
  }

  return {
    promise: entry.promise,
    unsubscribe: () => {
      entry?.listeners.delete(onProgress)
    },
  }
}

export function prefetchCatalogGames(
  config: PkCasinoCatalogConfig,
  mode: CatalogFetchMode,
  handlers: CatalogLoadHandlers,
): void {
  const key = cacheKey(config.catalogId, mode)
  if (getCachedCatalogGames(config.catalogId, mode)?.length || inflightLoads.has(key)) return
  startInflightLoad(config, mode, handlers)
}
